"use client";

import { useEffect, useRef } from "react";

export default function MagicCursor() {
  const wandRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const mouse = useRef({ x: -100, y: -100 });
  const wand = useRef({ x: -100, y: -100 });
  const glow = useRef({ x: -100, y: -100 });
  const raf = useRef(0);
  const pressed = useRef(false);
  const visible = useRef(false);

  useEffect(() => {
    // Skip on touch-only devices
    if (window.matchMedia("(pointer: coarse)").matches && !window.matchMedia("(pointer: fine)").matches) return;

    // Hide native cursor
    const style = document.createElement("style");
    style.id = "magic-cursor-style";
    style.textContent = "*, *::before, *::after { cursor: none !important; }";
    document.head.appendChild(style);

    const show = () => {
      if (visible.current) return;
      visible.current = true;
      if (wandRef.current) wandRef.current.style.opacity = "1";
      if (glowRef.current) glowRef.current.style.opacity = "1";
    };

    const hide = () => {
      visible.current = false;
      if (wandRef.current) wandRef.current.style.opacity = "0";
      if (glowRef.current) glowRef.current.style.opacity = "0";
    };

    const onMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      show();
    };

    const tick = () => {
      if (visible.current) {
        // Wand: tight follow
        wand.current.x += (mouse.current.x - wand.current.x) * 0.5;
        wand.current.y += (mouse.current.y - wand.current.y) * 0.5;
        // Glow ring: springy trail
        glow.current.x += (mouse.current.x - glow.current.x) * 0.15;
        glow.current.y += (mouse.current.y - glow.current.y) * 0.15;

        const s = pressed.current ? 0.85 : 1;
        const gs = pressed.current ? 1.3 : 1;

        if (wandRef.current)
          wandRef.current.style.transform = `translate3d(${wand.current.x}px,${wand.current.y}px,0) scale(${s})`;
        if (glowRef.current)
          glowRef.current.style.transform = `translate3d(${glow.current.x}px,${glow.current.y}px,0) scale(${gs})`;
      }
      raf.current = requestAnimationFrame(tick);
    };

    raf.current = requestAnimationFrame(tick);

    const down = () => { pressed.current = true; };
    const up = () => { pressed.current = false; };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.documentElement.addEventListener("mouseleave", hide);
    document.documentElement.addEventListener("mouseenter", () => show());

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.documentElement.removeEventListener("mouseleave", hide);
      const s = document.getElementById("magic-cursor-style");
      if (s) s.remove();
    };
  }, []);

  return (
    <>
      {/* Glow ring */}
      <div
        ref={glowRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 36,
          height: 36,
          marginLeft: -18,
          marginTop: -18,
          pointerEvents: "none",
          zIndex: 99998,
          opacity: 0,
          willChange: "transform",
          borderRadius: "50%",
          border: "1.5px solid rgba(139,92,246,0.35)",
          background: "radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)",
          boxShadow: "0 0 16px rgba(139,92,246,0.12)",
          transition: "opacity 0.25s, box-shadow 0.15s",
        }}
      />

      {/* Wand SVG */}
      <div
        ref={wandRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          pointerEvents: "none",
          zIndex: 99999,
          opacity: 0,
          willChange: "transform",
          transition: "opacity 0.25s",
          marginLeft: -3,
          marginTop: -2,
        }}
      >
        <svg
          width="38"
          height="38"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: "drop-shadow(0 0 3px rgba(139,92,246,0.45))" }}
        >
          <line x1="4" y1="4" x2="30" y2="30" stroke="url(#wg)" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="4" cy="4" r="2.5" fill="url(#tg)" opacity="0.9" />
          <path d="M4 1 L4.6 3.2 L7 4 L4.6 4.8 L4 7 L3.4 4.8 L1 4 L3.4 3.2Z" fill="#fff" opacity="0.9" />
          <defs>
            <linearGradient id="wg" x1="4" y1="4" x2="24" y2="24" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#2016F9" />
            </linearGradient>
            <radialGradient id="tg" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#fff" />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    </>
  );
}
